import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Send } from "lucide-react";
import { api } from "../lib/api";

export default function Comments({ slug }) {
    const { t } = useTranslation();
    const [comments, setComments] = useState([]);
    const [form, setForm] = useState({ name: "", email: "", content: "" });
    const [status, setStatus] = useState("");
    const [sending, setSending] = useState(false);

    const load = () => api.get(`/posts/${slug}/comments`)
        .then((r) => setComments(r.data))
        .catch(() => setComments([]));

    useEffect(() => { load(); }, [slug]);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        setStatus("");
        try {
            await api.post(`/posts/${slug}/comments`, { ...form, post_slug: slug });
            setStatus("pending");
            setForm({ name: "", email: "", content: "" });
        } catch (err) {
            const d = err.response?.data?.detail;
            setStatus(typeof d === "string" ? d : "Could not post comment");
        }
        setSending(false);
    };

    return (
        <section className="max-w-3xl mx-auto px-6 lg:px-12 pb-24" data-testid="comments-section">
            <div className="border-t border-femcare-border pt-12 mt-12">
                <div className="flex items-center gap-3 mb-8">
                    <MessageCircle className="w-5 h-5 text-femcare-pink" />
                    <h3 className="font-serif text-2xl text-femcare-purple">
                        {t("blog.comments")} <span className="text-femcare-purple/40 text-base">({comments.length})</span>
                    </h3>
                </div>

                {comments.length > 0 && (
                    <div className="space-y-5 mb-12">
                        {comments.map((c) => (
                            <div key={c.id} className="bg-white border border-femcare-border rounded-2xl p-5" data-testid={`comment-${c.id}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-9 h-9 rounded-full bg-femcare-pink/15 flex items-center justify-center text-sm font-medium text-femcare-pink">
                                        {(c.name || "?").charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <p className="font-medium text-femcare-purple text-sm">{c.name}</p>
                                        <p className="text-xs text-femcare-purple/50">
                                            {new Date(c.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                                        </p>
                                    </div>
                                </div>
                                <p className="text-femcare-purple/80 leading-relaxed whitespace-pre-line">{c.content}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="bg-femcare-petal/30 border border-femcare-border rounded-2xl p-6 md:p-8">
                    <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent mb-3">{t("blog.leaveComment")}</p>
                    <form onSubmit={submit} className="space-y-4" data-testid="comment-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input required value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder={t("blog.commentName")}
                                className="w-full bg-white border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                data-testid="comment-name" />
                            <input required type="email" value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder={t("blog.commentEmail")}
                                className="w-full bg-white border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                data-testid="comment-email" />
                        </div>
                        <textarea required rows={4} value={form.content}
                            onChange={(e) => setForm({ ...form, content: e.target.value })}
                            placeholder={t("blog.commentMessage")}
                            className="w-full bg-white border border-femcare-border rounded-xl px-4 py-3 resize-none focus:outline-none focus:border-femcare-pink"
                            data-testid="comment-content" />
                        <button type="submit" disabled={sending} className="btn-primary disabled:opacity-50" data-testid="comment-submit">
                            <Send className="w-4 h-4" />
                            {sending ? "..." : t("blog.commentSubmit")}
                        </button>
                        {status === "pending" && (
                            <p className="text-sm text-femcare-accent" data-testid="comment-status">{t("blog.commentPending")}</p>
                        )}
                        {status && status !== "pending" && (
                            <p className="text-sm text-red-600" data-testid="comment-error">{status}</p>
                        )}
                    </form>
                </div>
            </div>
        </section>
    );
}
